import React, { useEffect, useRef, useState, FC } from 'react';
import ReactPlayer from 'react-player';
import { Breakpoints } from '../../../constants/breakpoints.constants';
import { iconSize } from '../../../constants/icon.constants';
import useMediaQuery from '../../../lib/custom-hooks/useMediaQuery';
import { googleClickOpenVideoEvent } from '../../../utils/analytics';
import Icon from '../icon';
import Picture from '../picture';
import { VideoPlayerProps } from './types/videoPlayer.types';
import LazyLoad from 'react-lazyload';

const VideoPlayer: FC<VideoPlayerProps> = ({
  link,
  playIconClickCallback,
  playCallback,
  pauseCallback,
  endCallback,
  controls = true,
  autoPlay = false,
  muted = false,
  width = '100%',
  height = '100%',
  showThumbnail = false,
  extraClassName = '',
  iconWrapperClassName = '',
  videoPlayerWrapperClassName = '',
  mediaTitle = '',
  useAnalytics = false,
  mobilePlayIconSize = iconSize.xxs,
  desktopPlayIconSize = iconSize.xlg,
  editorialImageProps,
  productImageProps,
  activeCard = true,
  loop = false,
  playsInline,
}) => {
  const isDesktop = useMediaQuery(`(min-width: ${Breakpoints['tbl-p']}px)`);
  const [hideThumbnail, setHideThumbnail] = useState(false);
  const [playing, setPlaying] = useState(autoPlay ? true : false);

  const [onScreen, setOnScreen] = useState(false);
  const [runOnReady, setRunOnReady] = useState(false);
  const videoPlayerWrapperRef = useRef<any>(null);
  const videoPlayerRef = useRef<any>(null);
  const loadedSecondsRef = useRef<number>(0);

  useEffect(() => {
    const options = {
      rootMargin: '10px',
      threshold: 0.01,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (!entry) return;
        setOnScreen(entry.isIntersecting);
      });
    };

    const observer = new IntersectionObserver(observerCallback, options);

    if (videoPlayerWrapperRef?.current) {
      observer.observe(videoPlayerWrapperRef?.current);
    }

    return () => {
      if (videoPlayerWrapperRef?.current) {
        observer.unobserve(videoPlayerWrapperRef?.current);
      }
    };
  }, [videoPlayerWrapperRef, setOnScreen]);

  useEffect(() => {
    setOnScreen(activeCard);
  }, [activeCard]);

  useEffect(() => {
    if (link) {
      setHideThumbnail(false);
    }
  }, [link]);

  useEffect(() => {
    if (!onScreen) {
      setRunOnReady(true);
    }
  }, [onScreen]);

  const localPlayCallback = (): void => {
    setPlaying(true);
    if (playCallback) {
      playCallback();
    }
    useAnalytics && !playing && googleClickOpenVideoEvent(mediaTitle || editorialImageProps?.alt || productImageProps?.alt);
  };

  const localPauseCallback = (): void => {
    setPlaying(false);
    if (pauseCallback) {
      pauseCallback();
    }
  };

  const localEndCallback = (): void => {
    setPlaying(false);
    if (endCallback) {
      endCallback();
    }
  };

  const handlePlayButton = () => {
    playIconClickCallback?.();
    useAnalytics && playing && googleClickOpenVideoEvent(mediaTitle || editorialImageProps?.alt || productImageProps?.alt);
    setHideThumbnail(true);
  };

  const loadLastLoadedSecond = () => {
    if (!runOnReady) return;
    videoPlayerRef?.current?.seekTo(loadedSecondsRef.current, 'seconds');
    setRunOnReady(false);
  };

  const playIconSize = isDesktop ? desktopPlayIconSize : mobilePlayIconSize;

  const localEditorialImageProps = editorialImageProps
    ? {
        ...editorialImageProps,
        isVideoThumbnail: true,
        cssClassImageEl: (editorialImageProps?.cssClassImageEl || '') + 'video-player',
      }
    : undefined;

  const localProductImageProps = productImageProps
    ? {
        ...productImageProps,
        isVideoThumbnail: true,
        imageClassName: (productImageProps?.imageClassName || '') + 'video-player',
      }
    : undefined;

  const reactPlayerConfig = {
    file: {
      attributes: {
        preload: 'metadata',
      },
    },
    youtube: {
      playerVars: { autoplay: autoPlay ? 1 : 0 },
    },
  };

  return (
    <div style={{ width: '100%', height: '100%' }} ref={videoPlayerWrapperRef} className={videoPlayerWrapperClassName}>
      {showThumbnail && !hideThumbnail ? (
        <div className="video-player">
          <div className={iconWrapperClassName ? iconWrapperClassName : 'video-player__play-icon'} onClick={handlePlayButton}>
            <Icon iconName="action-play" size={playIconSize} color="primary" />
          </div>
          {(localEditorialImageProps || localProductImageProps) && (
            <Picture editorialImageProps={localEditorialImageProps} productImageProps={localProductImageProps} />
          )}
        </div>
      ) : (
        <LazyLoad offset={0} once={true} style={{ width: '100%', height: '100%' }}>
          {onScreen ? (
            <ReactPlayer
              url={link}
              controls={controls}
              onReady={loadLastLoadedSecond}
              onPlay={localPlayCallback}
              onPause={localPauseCallback}
              onEnded={localEndCallback}
              onProgress={(played) => {
                loadedSecondsRef.current = played.playedSeconds;
              }}
              playsinline={playsInline}
              width={width}
              height={height}
              muted={muted}
              loop={loop}
              className={`video-player ${extraClassName}`}
              playing={onScreen && playing}
              ref={videoPlayerRef}
              config={reactPlayerConfig}
            />
          ) : (
            <div style={{ width: '100%', height: '100%' }}></div>
          )}
        </LazyLoad>
      )}
    </div>
  );
};
export default React.memo(VideoPlayer);
